import React, { memo } from 'react';
import classNames from 'classnames';

import { getIn } from '../../../utils/ChangeSpy';
import FormSubmitOnBlur from '../../helpers/FormSubmitOnBlur';
import FormGroup from '../../helpers/FormGroup';
import Checkbox from '../../helpers/Checkbox';
import DropDown, { DropDownElement } from '../../helpers/DropDown';
import BaseInput from '../../helpers/Input';
import { IconButton } from '../../helpers/IconButton';
import ErrorDisplay from '../../decorators/DisplayError';
import PrettyErrorDecorator from '../../decorators/PrettyError';
import ContactTypedInfoTable from '../ContactTypedInfoTable';
import { useEntityEventContext } from '../../../contexts/EntityEventContext';

import style from './style.module.css';

const Input = PrettyErrorDecorator(ErrorDisplay(BaseInput));

/**
 * Stateless component for contact's info.
 * Also used to change contact info.
 */
const ContactInfoComponent = ({ info, onChange, onBlur, onSubmit, suffixSuggest }) => {
  const {
    onAddAddress,
    onAddEmail,
    onAddPhone,
    onEditAddress,
    onEditEmail,
    onEditPhone,
    onChangePhone,
    onChangeEmail,
    onChangeAddress,
    onAddressDelete,
    onPhoneDelete,
    onEmailDelete,
    onSortPhone,
    onSortEmail,
  } = useEntityEventContext();
  const phones = info.get('phones');
  const emails = info.get('emails');
  const addresses = info.get('addresses');

  const prefix = info.get('prefix');
  const prefixes = info.get('prefixes');

  const prefixesContent = prefixes.map((prefix, i) => {
    const attributes = prefix.get('attributes').toJS();

    return (
      <DropDownElement {...attributes} key={i} value={prefix.get('value')}>
        {prefix.get('name')}
      </DropDownElement>
    );
  });

  const age = Number(getIn(info, 'age'));
  const linkedin = getIn(info, 'linkedin') || '';
  const ageWrapperClassName = classNames(style.age, {
    [style.ageWarning]: age >= 60 && age < 65,
    [style.ageDanger]: age >= 65,
  });

  const doNotMailClassName = info.getIn(['doNotMail', 'value']) ? 'text-info' : '';

  return (
    <div className={style.host} onChange={onChange} role="form">
      <div className={style.fields}>
        <FormSubmitOnBlur
          className={classNames(style.form, 'information')}
          onComponentBlur={onBlur}
          onSubmit={onSubmit}
        >
          <div className={style.nameRow}>
            <div className={style.prefix}>
              <DropDown
                buttonContent={prefix}
                className="btn-default btn-xs full-width text-right"
                id="dropdownMenu7"
                name="prefix"
                onChange={onChange}
                root={info}
                deep
                isValuePicker
              >
                {prefixesContent}
              </DropDown>
            </div>
            <FormGroup className={style.fullName}>
              <Input
                id="w-full-name7"
                label="Full Name"
                name="fullName"
                placeholder="Full Name"
                root={info}
                deep
                showLabel
              />
            </FormGroup>
            <div className={style.suffix}>{suffixSuggest}</div>
          </div>

          <FormGroup>
            <Input
              id="f-f-name"
              label="First Name"
              name="firstName"
              placeholder="First Name"
              root={info}
              deep
              showLabel
            />
          </FormGroup>

          <FormGroup>
            <Input
              id="first name"
              label="Last Name"
              name="lastName"
              placeholder="Last Name"
              root={info}
              deep
              showLabel
            />
          </FormGroup>

          <FormGroup className={style.wide}>
            <Checkbox
              deep="checked"
              id="f-dnm"
              label="DNM"
              labelClassName={doNotMailClassName}
              name="doNotMail"
              root={info}
              boldOnChecked
            />
          </FormGroup>
        </FormSubmitOnBlur>

        <FormSubmitOnBlur
          className={classNames(style.form, 'information')}
          onComponentBlur={onBlur}
          onSubmit={onSubmit}
        >
          <FormGroup>
            <Input id="f-nick3" label="Nick" name="nick" placeholder="Nick" root={info} deep showLabel />
          </FormGroup>
          <FormGroup>
            <Input
              id="f-pronounce3"
              label="Pronunciation"
              name="pronounce"
              placeholder="Pronunciation"
              root={info}
              deep
              showLabel
            />
          </FormGroup>
          <FormGroup className={style.linkedin}>
            <Input id="f-linkedIn" label="LinkedIn" name="linkedin" placeholder="LinkedIn" root={info} deep showLabel />
            <IconButton
              className={style.linkedinLink}
              href={/^https?:\/\//i.test(linkedin) ? linkedin : `https://${linkedin}`}
              icon="linkedin-square"
              size="sm"
              title="Open LinkedIn"
            />
          </FormGroup>
          <FormGroup className={ageWrapperClassName}>
            <Input id="f-age" label="Age" name="age" placeholder="Age" root={info} deep showLabel />
          </FormGroup>
        </FormSubmitOnBlur>
      </div>

      <div className={style.tables}>
        <FormSubmitOnBlur className={style.table} onComponentBlur={onBlur} onSubmit={onSubmit}>
          <ContactTypedInfoTable
            contacts={phones}
            onAdd={onAddPhone}
            onChange={onChangePhone}
            onClick={onEditPhone}
            onDelete={onPhoneDelete}
            onSort={onSortPhone}
            type="Phones"
          />
        </FormSubmitOnBlur>

        <FormSubmitOnBlur className={style.table} onComponentBlur={onBlur} onSubmit={onSubmit}>
          <ContactTypedInfoTable
            contacts={emails}
            onAdd={onAddEmail}
            onChange={onChangeEmail}
            onClick={onEditEmail}
            onDelete={onEmailDelete}
            onSort={onSortEmail}
            type="Emails"
          />
        </FormSubmitOnBlur>

        <FormSubmitOnBlur
          className={classNames(style.table, style.wide)}
          onComponentBlur={onBlur}
          onSubmit={onSubmit}
          focusOnClick
        >
          <ContactTypedInfoTable
            contacts={addresses}
            onAdd={onAddAddress}
            onBlur={onBlur}
            onChange={onChangeAddress}
            onClick={onEditAddress}
            onDelete={onAddressDelete}
            type="Addresses"
          />
        </FormSubmitOnBlur>
      </div>
    </div>
  );
};

export const ContactInfo = memo(ContactInfoComponent);
